/**
 * Acceso al **portal del cliente** (`/login`).
 *
 * Es el portado de `PortalCliente::login()`: el cliente entra con su **número de
 * documento** y la contraseña que le genera el administrador desde su ficha del
 * panel. No hay registro ni recuperación de contraseña: si no entra, el
 * administrador se la vuelve a generar.
 *
 * Diferencias con el panel antiguo: allí, un documento sin portal activo o sin
 * contraseña daba mensajes distintos («Su cuenta no tiene acceso al portal»), lo
 * que permitía averiguar qué documentos son clientes. Aquí sólo se distingue el
 * caso de credenciales incorrectas y el de cuenta sin acceso (que el cliente debe
 * conocer para pedir ayuda), sin revelar si el documento existe.
 *
 * Sobre el diseño: antes era una pared turquesa con el logo flotando y una tarjeta
 * blanca suelta, que no tenía nada que ver con el interior del portal. Ahora es un
 * acceso en dos paneles —el de marca, con lo que el cliente encuentra dentro, y el
 * del formulario— y el lado del formulario usa el mismo fondo y las mismas
 * tarjetas que el resto de la aplicación.
 */

import {
  Car,
  Fuel,
  IdCard,
  Lock,
  LogIn,
  MailOpen,
  Receipt,
  Send,
  User,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Logo } from '@/components/brand/logo';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { APP_META } from '@/config/app';
import { env } from '@/config/env';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';

import { portalApi } from '../portal.api';
import { usePortalStore } from '../portal.store';

interface EstadoUbicacion {
  from?: string;
}

/** Lo que el cliente encuentra dentro del portal (panel de marca). */
const SECCIONES: readonly { icono: LucideIcon; etiqueta: string }[] = [
  { icono: Receipt, etiqueta: 'Comprobantes' },
  { icono: Fuel, etiqueta: 'Consumos' },
  { icono: Send, etiqueta: 'Despachos en vivo' },
  { icono: Wallet, etiqueta: 'Créditos y saldos' },
  { icono: Car, etiqueta: 'Tus vehículos' },
  { icono: IdCard, etiqueta: 'Tus conductores' },
  { icono: MailOpen, etiqueta: 'Mensajes a la estación' },
];

interface EstadoUbicacion {
  from?: string;
}

export function PortalLoginPage() {
  useDocumentTitle('Portal del cliente');
  const navegar = useNavigate();
  const ubicacion = useLocation();
  const iniciarSesion = usePortalStore((estado) => estado.iniciarSesion);

  const [numeroDoc, setNumeroDoc] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const destino = (ubicacion.state as EstadoUbicacion | null)?.from ?? '/resumen';

  const enviar = async (evento: React.FormEvent) => {
    evento.preventDefault();
    setError(null);

    if (!numeroDoc.trim() || !password) {
      setError('Ingresa tu número de documento y tu contraseña.');
      return;
    }

    setEnviando(true);
    try {
      const sesion = await portalApi.iniciarSesion(numeroDoc.trim(), password);
      iniciarSesion(sesion.cliente, sesion.tokens.accessToken);
      navegar(destino, { replace: true });
    } catch (fallo) {
      setError(
        fallo instanceof ApiError
          ? fallo.message
          : 'No se pudo conectar con el servidor. Intenta nuevamente.',
      );
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col lg:grid lg:grid-cols-2">
      {/* Panel de marca: qué encuentra el cliente dentro del portal. */}
      <aside className="relative isolate flex flex-col justify-between overflow-hidden bg-gradient-to-br from-brand-600 via-brand-800 to-ink-950 px-5 py-6 text-white sm:px-8 lg:px-12 lg:py-12">
        <div aria-hidden="true" className="absolute inset-0 bg-grid opacity-25" />
        <div
          aria-hidden="true"
          className="portal-halo -top-24 -left-24 opacity-40"
          style={{ background: 'radial-gradient(circle at 35% 35%, var(--color-brand-300), transparent 70%)' }}
        />

        <div className="relative">
          <Logo tone="light" size="lg" tagline="Estaciones de servicio" />

          <h1 className="mt-6 text-xl font-bold tracking-tight text-balance sm:text-2xl lg:mt-10">
            Tu cuenta de la estación, cuando la necesites
          </h1>
          <p className="mt-2 max-w-md text-sm text-white/75">
            Revisa tus comprobantes y consumos, mira cómo va tu crédito y escríbenos sin tener que
            llamar.
          </p>

          {/* Las secciones sólo se listan cuando hay sitio; en móvil basta el formulario. */}
          <ul className="mt-8 hidden grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid">
            {SECCIONES.map((seccion) => (
              <li
                key={seccion.etiqueta}
                className="flex items-center gap-2.5 rounded-card bg-white/10 px-3 py-2 text-sm font-medium text-white/90 ring-1 ring-white/10 backdrop-blur-sm"
              >
                <seccion.icono className="size-4 shrink-0 text-brand-200" aria-hidden="true" />
                {seccion.etiqueta}
              </li>
            ))}
          </ul>
        </div>

        <p className="relative mt-6 hidden text-[11px] text-white/50 lg:block">
          © {new Date().getFullYear()} {APP_META.vendor} · {APP_META.name}
        </p>
      </aside>

      {/* Formulario. */}
      <main className="portal-fondo relative flex flex-1 items-center justify-center px-4 py-10 sm:px-8">
        <div className="w-full max-w-md">
          <form
            onSubmit={enviar}
            className="rounded-card border border-ink-100/80 bg-white/95 p-6 shadow-card backdrop-blur-sm sm:p-7"
          >
            <div className="flex items-center gap-3">
              <span className="flex size-11 shrink-0 items-center justify-center rounded-card bg-gradient-to-br from-brand-500 to-accent-500 text-white shadow-brand">
                <LogIn className="size-5" aria-hidden="true" />
              </span>
              <div>
                <h2 className="text-lg font-bold tracking-tight text-ink-900">Iniciar sesión</h2>
                <p className="text-xs text-ink-500">Con tu documento y la contraseña del portal.</p>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-4">
              {error ? <Alert variant="error">{error}</Alert> : null}

              <Input
                label="Número de documento"
                value={numeroDoc}
                autoComplete="username"
                placeholder="20123456789"
                maxLength={13}
                leftIcon={<User className="size-4" aria-hidden="true" />}
                onChange={(evento) => setNumeroDoc(evento.target.value)}
              />

              <Input
                label="Contraseña"
                type="password"
                value={password}
                autoComplete="current-password"
                placeholder="Tu contraseña"
                leftIcon={<Lock className="size-4" aria-hidden="true" />}
                onChange={(evento) => setPassword(evento.target.value)}
              />

              <Button
                type="submit"
                size="lg"
                className="w-full"
                loading={enviando}
                leftIcon={<LogIn className="size-4" aria-hidden="true" />}
              >
                Entrar
              </Button>
            </div>

            <p className="mt-5 flex items-start gap-2 rounded-control bg-brand-50/70 px-3 py-2.5 text-[11px] leading-relaxed text-ink-600">
              <IdCard className="mt-0.5 size-3.5 shrink-0 text-brand-500" aria-hidden="true" />
              Si no tienes contraseña o no puedes entrar, pide al administrador de la estación que
              active tu acceso al portal.
            </p>
          </form>

          {env.panelUrl ? (
            <p className="mt-5 text-center text-[11px] text-ink-500">
              ¿Eres del personal de la estación?{' '}
              <a href={env.panelUrl} className="font-semibold text-brand-700 hover:underline">
                Entrar al panel
              </a>
            </p>
          ) : null}
        </div>
      </main>
    </div>
  );
}
